// @flow
import styled from 'styled-components';

// ── Page wrapper ─────────────────────────────────────────────────────────────
export const WalletPage = styled.div`
  margin-top: 60px;
  @media screen and (max-width: 620px) {
    padding: 0 10px;
  }
`;

// ── Page header row ───────────────────────────────────────────────────────────
export const WalletHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h6 {
    font-size: 20px;
    font-family: rebondG-Semi-Bold;
    color: #191919;
    margin: 0;
  }
`;

export const WalletHeaderBadge = styled.small`
  font-size: 12px;
  color: #6b7a99;
  background: #f0f3fb;
  padding: 5px 14px;
  border-radius: 20px;
  border: 1px solid #e0e6f4;
  letter-spacing: 0.3px;
`;

// ── Main unit-balance card ─────────────────────────────────────────────────────
// Uses styled-components so styles are hashed and can never clash with
// referral.scss or any other page that happens to name its card the same.
export const BalanceCard = styled.div`
  min-height: 146px;
  border-radius: 20px;
  padding: 28px;
  border: none;
  background: linear-gradient(140deg, #0a1628 0%, #1a3568 40%, #2f67d8 100%);
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.12),
    0 8px 24px rgba(0, 0, 0, 0.22),
    0 20px 56px rgba(47, 103, 216, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  color: #fff;
  position: relative;
  overflow: hidden;
  transition: transform 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 4px 8px rgba(0, 0, 0, 0.16),
      0 14px 32px rgba(0, 0, 0, 0.28),
      0 28px 64px rgba(47, 103, 216, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  &::before {
    content: '';
    position: absolute;
    right: -70px;
    top: -70px;
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.07) 0%, transparent 70%);
    border: 1px solid rgba(255, 255, 255, 0.06);
    pointer-events: none;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    left: -50px;
    bottom: -70px;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.04) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  > div {
    position: relative;
    z-index: 1;
  }

  h6 {
    color: rgba(255, 255, 255, 0.65);
    font-size: 12px;
    letter-spacing: 0.5px;
    margin: 0 0 8px;
  }

  h1 {
    font-family: rebondG-Bold;
    color: #fff;
    font-size: 38px;
    line-height: 1;
    margin: 0;
  }

  /* "Top up balance" trigger button — white pill on the dark card */
  button {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 50px;
    color: #2f67d8;
    font-family: rebondG-Semi-Bold;
    font-size: 13px;
    padding: 10px 24px;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18), 0 1px 3px rgba(0, 0, 0, 0.12);
    transition: all 0.18s ease;
    margin-top: 0;

    &:hover {
      background: #fff;
      transform: translateY(-1px);
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22), 0 2px 6px rgba(0, 0, 0, 0.14);
    }
  }
`;

// ── Tab bar wrapper (overrides global analysis-dashboard.scss within wallet) ──
export const WalletTabContainer = styled.div`
  margin-top: 28px;

  .tabs {
    border-bottom: 2px solid #edf0f7;
    gap: 4px;
  }

  .tab {
    font-size: 15px;
    color: #8a94a6;
    padding-bottom: 13px;
    width: auto;
    padding-right: 28px;
    transition: color 0.15s ease;

    &:hover {
      color: #4a5568;
    }
  }

  button.active2 {
    color: #191919;
    border-bottom: 3px solid #2f67d8;
    font-size: 15px;
    font-family: rebondG-Semi-Bold;
  }
`;

// ── Activity feed items ───────────────────────────────────────────────────────
export const ActivityCard = styled.div`
  display: flex;
  align-items: center;
  min-height: 86px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.055);
  border-left: ${({ $isSterling }) => $isSterling ? '3px solid #c81e34' : '1px solid rgba(0,0,0,0.055)'};
  border-radius: 14px;
  padding: 14px 20px;
  margin-top: 12px;
  transition: box-shadow 0.18s ease, transform 0.18s ease;

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(47, 103, 216, 0.1);
    transform: translateY(-1px);
  }

  @media screen and (max-width: 620px) {
    padding: 12px 14px;
  }
`;

export const ActivityIconCol = styled.div`
  flex: 0 0 52px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: 40px;
    width: 40px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

export const ActivityBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ActivityMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 3px;

  small {
    font-size: 11px;
    color: #191919;
    opacity: 0.4;
    letter-spacing: 0.2px;
  }
`;

export const ActivityDot = styled.span`
  color: #191919;
  opacity: 0.3;
  font-size: 14px;
  line-height: 1;
`;

export const ActivityTitle = styled.h6`
  font-size: 14px;
  margin: 2px 0 2px;
  color: #191919;
  font-family: rebondG-Semi-Bold;
`;

export const ActivityDesc = styled.p`
  font-size: 13px;
  color: #191919;
  opacity: 0.5;
  line-height: 18px;
  margin: 0;
`;

export const ActivityAmount = styled.div`
  text-align: right;
  flex-shrink: 0;
  padding-left: 12px;

  h6 {
    font-size: 14px;
    margin: 0 0 2px;
    font-family: rebondG-Semi-Bold;
  }

  p {
    font-size: 13px;
    color: #191919;
    opacity: 0.5;
    margin: 0;
  }
`;

export const SterlingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 100px;
  font-size: 10px;
  font-family: rebondG-Semi-Bold;
  background: rgba(200, 30, 52, 0.1);
  color: #c81e34;
  vertical-align: middle;
  border: 1px solid rgba(200, 30, 52, 0.15);
`;

// ── TopUp Modal ───────────────────────────────────────────────────────────────
export const TopUpOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 10, 20, 0.72);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 1200;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  @media screen and (min-width: 640px) {
    align-items: center;
  }
`;

export const TopUpSheet = styled.div`
  background: #fff;
  width: 100%;
  max-width: 480px;
  border-radius: 24px 24px 0 0;
  max-height: 94dvh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  animation: tuSlideUp 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
  -webkit-overflow-scrolling: touch;

  @keyframes tuSlideUp {
    from { transform: translateY(60px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }

  @media screen and (min-width: 640px) {
    border-radius: 24px;
    max-height: 88vh;
    animation: tuFadeScale 0.22s ease;

    @keyframes tuFadeScale {
      from { transform: scale(0.96); opacity: 0; }
      to   { transform: scale(1);    opacity: 1; }
    }
  }

  &::-webkit-scrollbar { width: 0; }
`;

export const TopUpHandle = styled.div`
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: #e2e8f0;
  margin: 12px auto 0;
  flex-shrink: 0;

  @media screen and (min-width: 640px) { display: none; }
`;

export const TopUpHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`;

export const TopUpHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const TopUpTitle = styled.h6`
  font-size: 17px;
  font-family: rebondG-Semi-Bold;
  color: #0f172a;
  margin: 0;
`;

export const TopUpCloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f1f5f9;
  border: none;
  color: #64748b;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease;

  &:hover { background: #e2e8f0; }
`;

export const TopUpBalancePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(47, 103, 216, 0.07);
  border: 1px solid rgba(47, 103, 216, 0.15);
  border-radius: 50px;
  padding: 5px 12px;
  margin-top: 10px;

  span:first-child {
    font-size: 11px;
    color: #64748b;
    letter-spacing: 0.3px;
  }

  span:last-child {
    font-size: 13px;
    font-family: rebondG-Semi-Bold;
    color: #2f67d8;
  }
`;

export const TopUpBody = styled.div`
  padding: 20px 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const TopUpMethodGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

export const TopUpMethodCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 14px 14px 12px;
  border-radius: 14px;
  border: 2px solid ${({ $active }) => $active ? '#2f67d8' : '#e2e8f0'};
  background: ${({ $active }) => $active ? 'rgba(47, 103, 216, 0.04)' : '#fafbfd'};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    border-color: ${({ $active }) => $active ? '#2f67d8' : '#cbd5e1'};
  }
`;

export const TopUpMethodIcon = styled.div`
  width: 100%;
  height: ${({ $square }) => $square ? '5rem' : '5rem'};
  border-radius: 10px;
  background: ${({ $active }) => $active ? 'rgba(47, 103, 216, 0.06)' : '#f4f6fb'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: background 0.15s ease;
  padding: ${({ $square }) => $square ? '8px' : '10px 12px'};

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

export const TopUpMethodName = styled.span`
  font-size: 12px;
  font-family: rebondG-Semi-Bold;
  color: ${({ $active }) => $active ? '#2f67d8' : '#475569'};
  line-height: 1.3;
  transition: color 0.15s ease;
`;

export const TopUpMethodDesc = styled.span`
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.3;
`;

export const TopUpFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const TopUpFieldLabel = styled.label`
  font-size: 12px;
  font-family: rebondG-Semi-Bold;
  color: #475569;
  letter-spacing: 0.3px;
`;

export const TopUpFieldInput = styled.input`
  height: 50px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 0 16px;
  font-size: 15px;
  color: #0f172a;
  background: #fafbfd;
  outline: none;
  width: 100%;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:focus {
    border-color: #2f67d8;
    background: #fff;
  }

  &::placeholder { color: #b0bac9; }
`;

export const TopUpAmountWrapper = styled.div`
  position: relative;
`;

export const TopUpAmountPrefix = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  font-family: rebondG-Semi-Bold;
  color: #475569;
  pointer-events: none;
`;

export const TopUpAmountInput = styled(TopUpFieldInput)`
  padding-left: 36px;
  font-size: 22px;
  font-family: rebondG-Semi-Bold;
  height: 58px;

  &::placeholder { font-size: 16px; font-family: rebondG-Regular, sans-serif; }
`;

export const TopUpFieldHint = styled.p`
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
  line-height: 1.4;
`;

export const TopUpFooter = styled.div`
  padding: 16px 24px max(16px, env(safe-area-inset-bottom));
  border-top: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
  background: #fff;
  position: sticky;
  bottom: 0;
`;

export const TopUpSendButton = styled.button`
  width: 100%;
  height: 54px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #1a3568 0%, #2f67d8 100%);
  color: #fff;
  font-size: 15px;
  font-family: rebondG-Semi-Bold;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(47, 103, 216, 0.35);
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    box-shadow: 0 6px 22px rgba(47, 103, 216, 0.45);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

export const TopUpCancelBtn = styled.button`
  width: 100%;
  height: 44px;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  background: transparent;
  color: #64748b;
  font-size: 14px;
  font-family: rebondG-Semi-Bold;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { background: #f8fafc; border-color: #cbd5e1; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
